import { useState, useEffect } from 'react';
import { Bell, Check, CheckCheck, Loader2, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { notificationAPI } from '@/lib/api';
import BottomNav from '@/components/BottomNav';

export default function EmployeeNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const res = await notificationAPI.getAll(50);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const markRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch {
      toast.error('Failed to mark all read');
    }
  };

  const typeColors = {
    success: 'bg-[#026536]/10 text-[#026536]',
    warning: 'bg-[#dea439]/10 text-[#dea439]',
    error: 'bg-red-50 text-red-500',
    info: 'bg-blue-50 text-blue-600',
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#f4f4f5] pb-24">
      <div className="bg-[#026536] pt-10 pb-8 px-5">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <p className="text-white/60 text-[10px] font-semibold uppercase tracking-wider">Notifications</p>
            <h1 data-testid="notifications-title" className="text-lg font-extrabold text-white tracking-tight">
              Alerts {unreadCount > 0 && <span className="text-sm font-normal text-white/60">({unreadCount} unread)</span>}
            </h1>
          </div>
          {unreadCount > 0 && (
            <Button
              data-testid="mark-all-read-btn"
              onClick={markAllRead}
              size="sm"
              className="bg-white/15 hover:bg-white/25 text-white text-xs h-8 gap-1"
            >
              <CheckCheck size={14} /> Mark All Read
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 -mt-4 max-w-lg mx-auto space-y-2">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-[#026536]" size={24} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <Inbox className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
            <p className="font-medium text-[#1a2529]/50">No notifications yet</p>
            <p className="text-xs text-[#1a2529]/30 mt-1">You'll see updates on leaves, sanctions and more here</p>
          </div>
        ) : (
          notifications.map((n, idx) => (
            <div
              key={n.id}
              data-testid={`notification-${idx}`}
              onClick={() => !n.read && markRead(n.id)}
              className={`bg-white rounded-xl p-4 shadow-sm transition-all cursor-pointer ${!n.read ? 'border-l-4 border-[#026536]' : 'border-l-4 border-transparent opacity-70'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${typeColors[n.type] || typeColors.info} border-0 text-[10px]`}>{n.type}</Badge>
                    <span className="text-[10px] text-[#1a2529]/40 font-medium">{formatTime(n.created_at)}</span>
                  </div>
                  <p className="text-sm font-semibold text-[#1a2529] leading-tight">{n.title}</p>
                  <p className="text-xs text-[#1a2529]/60 mt-0.5">{n.message}</p>
                </div>
                {!n.read && (
                  <div className="h-2.5 w-2.5 rounded-full bg-[#026536] shrink-0 mt-1.5" />
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
}
